/**
 * Whether the inline right sidebar may take a given width.
 *
 * The rule is about the composer, not the sidebar: widening the panel narrows
 * the transcript column, and past a point the composer's own controls stop
 * fitting. Deciding that needs measurements from a live layout, but the
 * decision itself is arithmetic — so it lives here, where it can be tested
 * against the cases that matter rather than only by dragging a real divider.
 *
 * Half-pixel tolerances throughout: `scrollWidth`/`clientWidth` are integers
 * while `getBoundingClientRect()` is fractional, so an element that fits
 * exactly can read as one sub-pixel too wide and reject a legal width.
 */
const SUBPIXEL_TOLERANCE_PX = 0.5;

export interface ComposerFitMeasurements {
  /** `getBoundingClientRect().width` of the composer form. */
  readonly formWidth: number;
  /** The viewport's width with its own padding removed. */
  readonly viewportContentWidth: number;
  readonly composerClientWidth: number;
  readonly composerScrollWidth: number;
  /** Below this the composer's own controls collide. */
  readonly minimumComposerWidth: number;
}

export function composerFitsAtSidebarWidth(measurements: ComposerFitMeasurements): boolean {
  const {
    formWidth,
    viewportContentWidth,
    composerClientWidth,
    composerScrollWidth,
    minimumComposerWidth,
  } = measurements;

  const overflowsItself = composerScrollWidth > composerClientWidth + SUBPIXEL_TOLERANCE_PX;
  const overflowsViewport = formWidth > viewportContentWidth + SUBPIXEL_TOLERANCE_PX;
  const belowMinimum = composerClientWidth + SUBPIXEL_TOLERANCE_PX < minimumComposerWidth;

  return !overflowsItself && !overflowsViewport && !belowMinimum;
}

/** The composer cannot go below its left controls plus its right actions. */
export function resolveMinimumComposerWidth(input: {
  readonly leftControlsWidth: number;
  readonly rightActionsWidth: number;
  readonly footerGap: number;
}): number {
  return input.leftControlsWidth + input.rightActionsWidth + input.footerGap;
}

/**
 * `column-gap` if the footer sets one, else the shorthand `gap`, else nothing.
 *
 * Both resolve to a length or to `normal`, and `Number.parseFloat("normal")` is
 * `NaN` — which would poison the minimum width rather than fall through.
 */
export function readFooterGap(style: { columnGap?: string; gap?: string } | null): number {
  if (!style) return 0;
  const column = Number.parseFloat(style.columnGap ?? "");
  if (Number.isFinite(column)) return column;
  const shorthand = Number.parseFloat(style.gap ?? "");
  return Number.isFinite(shorthand) ? shorthand : 0;
}

/** A box's inner width: `clientWidth` less its own horizontal padding. */
export function readContentWidth(input: {
  readonly clientWidth: number;
  readonly paddingLeft?: string;
  readonly paddingRight?: string;
}): number {
  const left = Number.parseFloat(input.paddingLeft ?? "") || 0;
  const right = Number.parseFloat(input.paddingRight ?? "") || 0;
  return Math.max(0, input.clientWidth - left - right);
}
